import React from "react";
import { ChevronRight, ChevronDown } from "lucide-react";

/* Recipe material picker, empty-query mode: the library folded into its
 * Settings categories. Click / Enter / → opens a category (one at a time),
 * ↑ ↓ walk categories and the open category's materials, Enter picks.
 * `groupMaterials` + `treeRows` are shared with the keyboard handler in
 * RecipeBuilder.MaterialAutocomplete so both agree on what is visible. */

export function groupMaterials(options, categories) {
  const byKey = new Map();
  for (const o of options || []) {
    const k = o.category || "";
    if (!byKey.has(k)) byKey.set(k, []);
    byKey.get(k).push(o);
  }
  const order = (categories || []).map((c) => c.key);
  const labelOf = (k) => (categories || []).find((c) => c.key === k)?.label || k || "No category";
  return [...byKey.entries()]
    .map(([key, items]) => ({ key, label: labelOf(key), items: items.slice().sort((a, b) => a.name.localeCompare(b.name)) }))
    .sort((a, b) => {
      if (!a.key) return 1;
      if (!b.key) return -1;
      const ia = order.indexOf(a.key), ib = order.indexOf(b.key);
      return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib) || a.label.localeCompare(b.label);
    });
}

/** The rows the eye (and the arrow keys) walk: every category, and the open one's materials right after it. */
export function treeRows(groups, openKey) {
  const rows = [];
  for (const g of groups) {
    rows.push({ type: "cat", group: g });
    if (g.key === openKey) for (const o of g.items) rows.push({ type: "item", material: o, group: g });
  }
  return rows;
}

export function MaterialCategoryTree({ rows, openKey, highlight = -1, onToggle, onSelect, onHover, testId = "material-picker" }) {
  if (!rows.length) return null;
  return (
    <div data-testid={`${testId}-tree`} role="listbox">
      <div className="px-3 pt-1.5 pb-1 text-ink-4" style={{ fontSize: 10.5 }}>Pick a category, or just start typing · ↑ ↓ move, → opens, Enter picks</div>
      {rows.map((r, i) => {
        const hi = i === highlight;
        if (r.type === "cat") {
          const g = r.group;
          const isOpen = openKey === g.key;
          const Chev = isOpen ? ChevronDown : ChevronRight;
          return (
            <button key={`cat-${g.key || "__none"}`} type="button" role="option" aria-expanded={isOpen} aria-selected={hi} data-hi={hi || undefined}
              onMouseDown={(e) => { e.preventDefault(); onToggle(g.key); }}
              onMouseEnter={() => onHover?.(i)}
              className={["w-full flex items-center gap-1.5 text-left px-3 py-1.5", hi ? "bg-accent-soft text-ink" : isOpen ? "bg-surface-2 text-ink" : "text-ink-2 hover:bg-surface-2"].join(" ")}
              style={{ fontSize: 12 }} data-testid={`${testId}-cat-${g.key || "none"}`}>
              <Chev size={12} className="text-ink-4 shrink-0" />
              <span className="flex-1 truncate" style={{ fontWeight: isOpen ? 600 : 500 }}>{g.label}</span>
              <span className="font-mono tnum text-ink-4" style={{ fontSize: 10.5 }}>{g.items.length}</span>
            </button>
          );
        }
        const o = r.material;
        return (
          <button key={o.id} type="button" role="option" aria-selected={hi} data-hi={hi || undefined}
            onMouseDown={(e) => { e.preventDefault(); onSelect(o); }}
            onMouseEnter={() => onHover?.(i)}
            className={["w-full text-left pl-8 pr-3 py-1.5", hi ? "bg-accent-soft text-ink" : "text-ink-2 hover:bg-accent-soft"].join(" ")}
            style={{ fontSize: 12 }} data-testid={`${testId}-item-${o.id}`}>
            {o.name} <span className="text-ink-4">({o.code})</span>
          </button>
        );
      })}
    </div>
  );
}
